import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  OnDestroy,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UavDataTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavDataType.service';
import { UavCameraTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavCameraType.service';
import { UavPhotoService } from '@6grain/masofa-monolith-api-client/uav/api/uavPhoto.service';
import { UavDataType } from '@6grain/masofa-monolith-api-client/dictionaries/model/uavDataType';
import { UavCameraType } from '@6grain/masofa-monolith-api-client/dictionaries/model/uavCameraType';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { TagSelectorComponent } from '@app/components/tag-selector/tag-selector.component';
import { ConfirmationModalComponent } from '../../Ui-Kit/confirmation-modal/confirmation-modal.component';
import { Subscription, timer, Subject } from 'rxjs';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {MsfInput} from '@components/ui/forms/input/input';
import {
  UAV_UPLOAD_ACCEPT,
  UAV_UPLOAD_FAILED,
  pickUavUploadFile,
  uavUploadFileFormat,
  uavServerErrorText,
} from './uav-upload-file.util';

@Component({
  selector: 'app-uav-upload-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ServerTranslatePipe,
    TagSelectorComponent,
    ConfirmationModalComponent,
    PrimaryButton,
    SecondaryButton,
    MsfInput,
  ],
  templateUrl: './uav-upload-modal.component.html',
  styleUrls: ['./uav-upload-modal.component.scss'],
})
export class UavUploadModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() uploadSuccess = new EventEmitter<void>();

  uploadForm!: FormGroup;
  nameControl!: FormControl;
  readonly uploadAccept = UAV_UPLOAD_ACCEPT;
  readonly selectedFile = signal<File | null>(null);
  readonly selectedFileFormat = computed(() => {
    const file = this.selectedFile();
    return file ? uavUploadFileFormat(file.name) : '';
  });
  isSubmitting: boolean = false;
  readonly uploadError = signal<string | null>(null);
  uploadProgress: number = 0;
  estimatedProcessingTime: number = 0;
  remainingTime: number = 0;
  private readonly PROCESSING_COEFFICIENT = 0.8 / (1024 * 1024);
  private timerSubscription: Subscription | null = null;

  isCancelConfirmVisible: boolean = false;

  uavDataTypes: UavDataType[] = [];
  cameraTypes: UavCameraType[] = [];
  selectedTagIds: string[] = [];

  private fb = inject(FormBuilder);
  private uavPhotoService = inject(UavPhotoService);
  private uavDataTypeService = inject(UavDataTypeService);
  private uavCameraTypeService = inject(UavCameraTypeService);
  private translate = inject(TranslateService);

  private uploadSubscription: Subscription | null = null;

  constructor() {}

  ngOnInit(): void {
    this.initForm();
    this.loadDictionaries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopProcessingTimer();
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
  }

  private initForm(): void {
    this.uploadForm = this.fb.group({
      name: ['', Validators.required],
      dataType: [null],
      cameraType: [null],
      tags: [[]],
      analysisOnly: [true],
      comment: [''],
    });
    this.nameControl = this.uploadForm.get('name') as FormControl;
  }

  private loadDictionaries(): void {
    this.uavDataTypeService
      .dictionariesUavDataTypeGetByQueryPost({})
      .subscribe({
        next: (res) => {
          this.uavDataTypes = res || [];
        },
        error: (err) => console.error('Error loading UAV Data Types:', err),
      });
    this.uavCameraTypeService
      .dictionariesUavCameraTypeGetByQueryPost({})
      .subscribe({
        next: (res) => {
          this.cameraTypes = res || [];
        },
        error: (err) => console.error('Error loading UAV Camera Types:', err),
      });
  }

  onTagIdsChange(tagIds: string[]): void {
    this.selectedTagIds = tagIds;
    this.uploadForm.patchValue({ tags: tagIds });
  }

  onClose(): void {
    if (this.isSubmitting) {
      this.isCancelConfirmVisible = true;
    } else {
      this.forceClose();
    }
  }

  forceClose(): void {
    this.stopProcessingTimer();

    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }

    this.isCancelConfirmVisible = false;
    this.close.emit();
    this.selectedTagIds = [];
    this.uploadForm.reset({ analysisOnly: true, tags: [] });
    this.selectedFile.set(null);
    this.isSubmitting = false;
    this.uploadProgress = 0;
    this.uploadError.set(null);
  }

  closeCancelConfirm(): void {
    this.isCancelConfirmVisible = false;
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    this.applyPickedFiles(element.files);
    // Allow picking the same file again after an error.
    element.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  async onFileDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    this.applyPickedFiles(event.dataTransfer?.files);
  }

  /**
   * Accepts exactly one zip or GeoTIFF; anything else clears the selection and shows an error.
   * Ignored while an upload is running, so the displayed file always matches the file being sent.
   */
  private applyPickedFiles(files: ArrayLike<File> | null | undefined): void {
    if (this.isSubmitting) {
      return;
    }
    const pick = pickUavUploadFile(files);
    if (pick.errorKey) {
      // Drop the previous selection so the visible error never sits next to a file that would still be sent.
      this.selectedFile.set(null);
      this.uploadError.set(this.translate.instant(pick.errorKey));
      return;
    }
    if (pick.file) {
      this.selectedFile.set(pick.file);
      this.uploadError.set(null);
      this.calculateEstimatedTime(pick.file.size);
    }
  }

  private calculateEstimatedTime(bytes: number): void {
    this.estimatedProcessingTime = Math.ceil(
      bytes * this.PROCESSING_COEFFICIENT
    );
    if (this.estimatedProcessingTime < 5) {
      this.estimatedProcessingTime = 5;
    }
    this.remainingTime = this.estimatedProcessingTime;
  }

  private startProcessingTimer(): void {
    this.stopProcessingTimer();
    this.timerSubscription = timer(0, 1000).subscribe(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
      }
    });
  }

  private stopProcessingTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  onUploadSubmit(): void {
    this.uploadForm.markAllAsTouched();
    if (this.uploadForm.get('name')?.invalid) {
      this.uploadError.set(this.translate.instant('ERROR_NAME_REQUIRED'));
      return;
    }
    const file = this.selectedFile();
    if (!file) {
      this.uploadError.set(this.translate.instant('ERROR_FILE_REQUIRED'));
      return;
    }
    this.isSubmitting = true;
    this.uploadError.set(null);
    this.uploadProgress = 0;
    this.remainingTime = this.estimatedProcessingTime;
    const formValues = this.uploadForm.value;
    const tagIds = Array.isArray(formValues.tags)
      ? formValues.tags.map((tag: { id?: string } | string) => (typeof tag === 'string' ? tag : (tag.id ?? '')))
      : undefined;

    // The generated client keeps the form field names (ZipFile, Name, ...) and reports upload progress.
    this.uploadSubscription = this.uavPhotoService
      .uavUavPhotoUploadSurveyArchivePost(
        file,
        formValues.comment || undefined,
        formValues.dataType || undefined,
        formValues.cameraType || undefined,
        !!formValues.analysisOnly,
        tagIds,
        (formValues.name || '').trim(),
        undefined,
        undefined,
        'events',
        true,
        { httpHeaderAccept: 'application/json' },
      )
      .subscribe({
        next: (event: HttpEvent<string>) => {
          if (event.type === HttpEventType.UploadProgress) {
            if (event.total) {
              this.uploadProgress = Math.round((100 * event.loaded) / event.total);
              if (this.uploadProgress === 100 && !this.timerSubscription) {
                this.startProcessingTimer();
              }
            }
          } else if (event.type === HttpEventType.Response) {
            this.stopProcessingTimer();
            this.isSubmitting = false;
            this.uploadSubscription = null;
            this.uploadSuccess.emit();
            this.onClose();
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error('Upload error', err);
          this.stopProcessingTimer();
          this.isSubmitting = false;
          this.uploadProgress = 0;
          // A 400 carries a readable reason (for example "EPSG:4326 required."); show it.
          this.uploadError.set(uavServerErrorText(err, (key) => this.translate.instant(key), UAV_UPLOAD_FAILED));
          this.uploadSubscription = null;
        },
      });
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
